import { sortBy } from 'lodash';
import type { OrderId, VoyageCharterId } from '../../../graphql';
import type { TimeData } from '../TimeData';
import {
  isDeckCargo,
  isDischargeEventTimeData,
  isLoadEventTimeData,
} from '../TimeData';
import type { Vessel } from '../Vessel';
import { getVesselCapacities } from '../Vessel';
import type { LoadedAmount } from '../../shipment/loadedAmounts';
import type { GetIncomeForLoadedAmount } from '../../shipment/income/income';
import {
  getConsumptionByPortCall,
  getPortCallIdsByOrder,
} from '../../shipment/income/baf';
import { buildOnBoardDraftSegmentsFromTimeData } from '../onBoardDraftSegments';
import type { OnBoardSegment } from '../onBoardDraftSegments';
import {
  bandContainsWholePiece,
  isPerPieceCargoUnit,
} from '../../../constants/cargoUnits';

/** orderId → target fill ratio in [0, 1] of the order's MAX quantity. The raise stays in
 * [floorRatio, 1]; the below-minimum reduce phase can drop a ratio below floorRatio toward 0. */
export type FillResult = Map<OrderId, number>;

type LoadedOrder = {
  orderId: OrderId;
  deckCargo: boolean;
  /** floorRatio·max consumption (NOT raw band min): the dimension-coupled at-floor load. */
  floorMass: number;
  floorHold: number;
  floorDeck: number;
  /** Raw band-minimum mass (for pcs/set the minimum piece count) — the band edge used to decide
   * whether the band spans a whole piece, independent of the binding dimension's floorRatio. */
  minMass: number;
  maxMass: number;
  maxHold: number;
  maxDeck: number;
  floorRatio: number;
  /** Whether this order has a genuine margin band (some dimension's max exceeds its min). */
  banded: boolean;
  /** Whether this order may be raised toward max (genuine band AND in raisable set). */
  raisable: boolean;
  /** For fixed (committed/no-band) orders: the actually-derived consumption to hold constant. */
  fixedMass: number;
  fixedHold: number;
  fixedDeck: number;
  loadedPcs: number;
  cargoAmount: number;
  cargoUnit: string | null;
  /** Priced/measured in discrete pieces or sets: its loaded quantity must land on a whole piece.
   * True iff the cargo unit is pcs/set — the same signal getOrderPcsOrSet uses to make td.pcs
   * meaningful, and the only unit for which an integer piece count exists to snap to. */
  perPiece: boolean;
  /** Position of this order in load order (first loaded = 0). The reduce phase breaks income ties by
   * reducing the last-loaded order first (LIFO). */
  loadIndex: number;
  voyageCharterId: VoyageCharterId | null;
};

/** Float slack absorbing rounding in maxMass·floorRatio (a 6-piece floor can derive as 5.9999997).
 * One part in ~1e6 of a piece — far below any real partial-piece tolerance. */
const PIECE_EPSILON = 1e-6;

/** Relative tolerance for treating a segment's at-floor usage as exceeding a capacity, absorbing
 * float noise in the maxMass·floorRatio sums. */
const CAP_RELATIVE_EPSILON = 1e-6;

/** For a per-piece order, maxMass (in system units) equals its MAX piece count, so loaded pieces at a
 * given ratio = maxMass·ratio. Snap the raised ratio DOWN to the largest whole piece count that is
 * still within the capacity/draft-feasible ratio and strictly above the (possibly fractional) floor
 * piece count; when none is reachable, return `fallbackRatio`. */
function snapRaiseToWholePiece(
  maxPieces: number,
  floorRatio: number,
  raisedRatio: number,
  fallbackRatio: number,
): number {
  const feasiblePieces = maxPieces * raisedRatio;
  const floorPieces = maxPieces * floorRatio;
  const wholePieces = Math.floor(feasiblePieces + PIECE_EPSILON);
  if (wholePieces <= floorPieces + PIECE_EPSILON) return fallbackRatio;
  return wholePieces / maxPieces;
}

/** The ratio a per-piece order falls back to when its raise cannot land on a whole piece above the
 * floor: its band floor when the band spans a whole piece (stay at the base load), or its entered
 * value when the band spans no whole piece. A band such as [10.2, 10.8] admits no integer, so a
 * fractional entered piece count is legitimate there and must be preserved rather than dropping to the
 * band minimum. For pcs/set cargoAmount is the entered piece count (identity system-unit conversion). */
function perPieceFallbackRatio(o: LoadedOrder): number {
  if (bandContainsWholePiece(o.minMass, o.maxMass)) return o.floorRatio;
  return o.maxMass > 0 ? Math.min(1, o.cargoAmount / o.maxMass) : o.floorRatio;
}

/** For a per-piece order reduced below floor, snap the reduced ratio DOWN to a whole piece count,
 * floored at zero. Snapping down only lowers the load, so it never reintroduces the overshoot the
 * reduction was clearing. */
function snapReduceToWholePiece(
  maxPieces: number,
  reducedRatio: number,
): number {
  const wholePieces = Math.max(
    0,
    Math.floor(maxPieces * reducedRatio + PIECE_EPSILON),
  );
  return wholePieces / maxPieces;
}

/** Amount by which a segment's used capacity exceeds `cap`, or 0 when within a relative tolerance.
 * An Infinity cap (no constraint) is never exceeded. */
function capOvershoot(used: number, cap: number): number {
  if (!Number.isFinite(cap)) return 0;
  return used - cap > CAP_RELATIVE_EPSILON * cap ? used - cap : 0;
}

/** Collect the orders that are both loaded AND discharged on this voyage, with their band floors and
 * capped/fixed consumption. `raisableOrderIds` selects which banded orders the raise may move. Gather
 * discharge order ids first so the load scan stays linear (a voyage loads each order before it). */
function collectLoadedOrders(
  timeData: TimeData[],
  raisableOrderIds: ReadonlySet<OrderId>,
): LoadedOrder[] {
  const dischargedOrderIds = new Set<OrderId>();
  for (const td of timeData) {
    if (isDischargeEventTimeData(td)) dischargedOrderIds.add(td.event.order.id);
  }
  const loadedOrders: LoadedOrder[] = [];
  for (const td of timeData) {
    if (!isLoadEventTimeData(td)) continue;
    const { order } = td.event;
    if (!dischargedOrderIds.has(order.id)) continue;

    const deckCargo = isDeckCargo(order.orderDetailsByOrderId.nodes);
    const maxMass = order.cargoRanges.mass.max;
    const maxVolume = order.cargoRanges.volume.max;
    const minMass = order.cargoRanges.mass.min;
    const minVolume = order.cargoRanges.volume.min;

    // floorRatio: the ratio at which the binding dimension equals its band minimum. A dimension with
    // a zero band max is absent (a mass-unit cargo with no stowage factor carries volume {0,0}, and
    // vice versa) and must not drive the floor — only when BOTH are absent is the order pinned at
    // ratio 1 (no fill slack).
    const massFloorRatio = maxMass > 0 ? minMass / maxMass : null;
    const volumeFloorRatio = maxVolume > 0 ? minVolume / maxVolume : null;
    const floorRatio =
      massFloorRatio === null && volumeFloorRatio === null
        ? 1
        : Math.max(massFloorRatio ?? 0, volumeFloorRatio ?? 0);

    const banded = maxMass > minMass || maxVolume > minVolume;
    const raisable = raisableOrderIds.has(order.id) && banded;

    const floorMass = maxMass * floorRatio;
    const floorVolume = maxVolume * floorRatio;

    loadedOrders.push({
      orderId: order.id,
      deckCargo,
      floorMass,
      floorHold: deckCargo ? 0 : floorVolume,
      floorDeck: deckCargo ? floorVolume : 0,
      minMass,
      maxMass,
      maxHold: deckCargo ? 0 : maxVolume,
      maxDeck: deckCargo ? maxVolume : 0,
      floorRatio,
      banded,
      raisable,
      // Fixed orders keep their actually-derived consumption (read from the timeData, not band min).
      fixedMass: td.mass,
      fixedHold: deckCargo ? 0 : td.volume,
      fixedDeck: deckCargo ? td.volume : 0,
      loadedPcs: td.pcs,
      cargoAmount: Number(order.cargoAmount),
      cargoUnit: order.cargoUnitByCargoUnitId?.name ?? null,
      perPiece: isPerPieceCargoUnit(order.cargoUnitByCargoUnitId?.name),
      loadIndex: loadedOrders.length,
      voyageCharterId:
        order.incomingVoyageCharterRelation?.voyageCharterId ?? null,
    });
  }
  return loadedOrders;
}

const EMPTY_ORDER_SET: ReadonlySet<OrderId> = new Set();

type SegmentCaps = {
  massCapacity: number;
  volumeCapacity: number;
  deckVolumeCapacity: number;
};

/** The vessel's capacities as caps: a dimension it does not define caps nothing.
 * spec: specs/concepts/cargo-load-derivation.md */
function getVesselCaps(vessel: Vessel): SegmentCaps {
  const { massCapacity, volumeCapacity, deckVolumeCapacity } =
    getVesselCapacities(vessel);
  return {
    massCapacity: massCapacity ?? Infinity,
    volumeCapacity: volumeCapacity ?? Infinity,
    deckVolumeCapacity: deckVolumeCapacity ?? Infinity,
  };
}

/** Whether any constant-on-board span exceeds an effective capacity when every order carries its band
 * floor: the span's mass ceiling is the tighter of vessel DWCC and the laden port-draft mass cap. */
function anySegmentOverCapAtFloor(
  segments: ReadonlyArray<OnBoardSegment>,
  orderById: Map<OrderId, LoadedOrder>,
  caps: SegmentCaps,
): boolean {
  for (const seg of segments) {
    let usedMass = 0;
    let usedHold = 0;
    let usedDeck = 0;
    for (const id of seg.onBoard) {
      const o = orderById.get(id);
      if (!o) continue;
      usedMass += o.floorMass;
      usedHold += o.floorHold;
      usedDeck += o.floorDeck;
    }
    const massCap = Math.min(caps.massCapacity, seg.draftMassCap);
    if (
      capOvershoot(usedMass, massCap) > 0 ||
      capOvershoot(usedHold, caps.volumeCapacity) > 0 ||
      capOvershoot(usedDeck, caps.deckVolumeCapacity) > 0
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Whether the loaded set cannot fit at band minimum under the effective per-segment capacities (mass,
 * laden port draft, hold and deck volume). Exported so the committed caller can decide whether to run
 * the below-minimum reduction without paying for any income evaluation — when it returns false the
 * reduce phase is a no-op and the fill behaves exactly as it did before draft-aware reduction existed.
 */
export function isMinFitInfeasibleUnderCaps(
  timeData: TimeData[],
  vessel: Vessel,
): boolean {
  const loadedOrders = collectLoadedOrders(timeData, EMPTY_ORDER_SET);
  if (loadedOrders.length === 0) return false;
  const orderById = new Map(loadedOrders.map((o) => [o.orderId, o]));
  const { segments } = buildOnBoardDraftSegmentsFromTimeData(timeData, vessel);
  return anySegmentOverCapAtFloor(segments, orderById, getVesselCaps(vessel));
}

/**
 * Income-prioritized fill for a candidate/committed voyage.
 *
 * DCAT loads ONE ratio across BOTH dimensions (massLoaded = maxMass·ratio, volumeLoaded =
 * maxVolume·ratio). At floorRatio = max(mass.min/mass.max, volume.min/volume.max) the load consumes
 * floorRatio·max on each dimension (≥ band min when the bands are asymmetric). The fill's capacity
 * baseline and raise-delta therefore BOTH use floorRatio·max, never the raw band min; otherwise the
 * accounting drifts and the greedy raises past true capacity.
 *
 * The raise seeds every raisable order at floor and moves it up by marginal freight income per tonne
 * (two whole-voyage income snapshots — all raisable at floor vs all at max), bounded per on-board span
 * by the shared mass/volume budget and by the laden port-draft mass cap. Lump-sum freight yields equal
 * floor/max income ⇒ zero marginal ⇒ it fills last.
 *
 * When `allowReduceBelowMin` is set and the set does not fit at band minimum (committed voyages only —
 * suggestions exclude such sets instead), a reduce phase runs first: it seeds every order at floor and
 * pushes the lowest marginal-income cargo below minimum toward zero (last-loaded first on an income
 * tie) until each over-full span clears. Orders reduced below floor are held out of the raise so the
 * two phases cannot oscillate.
 *
 * Greedy by marginal income per tonne is a documented single-resource heuristic; multi-resource
 * optimality is out of scope.
 */
export function computeIncomePrioritizedFillRatios(
  timeData: TimeData[],
  vessel: Vessel,
  getIncomeForLoadedAmount: GetIncomeForLoadedAmount,
  /** orderIds that are candidate assignments (raisable). On-board orders NOT in this set are
   * committed/pre-existing and are held at their already-derived td amount, never raised. */
  raisableOrderIds: ReadonlySet<OrderId>,
  /** Committed path only: permit the reduce phase to load cargo below band minimum, income-ordered,
   * when the set does not fit at minimum. Always false for suggestions. */
  allowReduceBelowMin: boolean,
): FillResult {
  const loadedOrders = collectLoadedOrders(timeData, raisableOrderIds);
  if (loadedOrders.length === 0) return new Map();

  const orderById = new Map(loadedOrders.map((o) => [o.orderId, o]));
  const { segments, segmentIndicesByOrderId } =
    buildOnBoardDraftSegmentsFromTimeData(timeData, vessel);
  const { massCapacity, volumeCapacity, deckVolumeCapacity } =
    getVesselCaps(vessel);

  const infeasibleAtFloor =
    allowReduceBelowMin &&
    anySegmentOverCapAtFloor(segments, orderById, {
      massCapacity,
      volumeCapacity,
      deckVolumeCapacity,
    });

  const raisableOrders = loadedOrders.filter((o) => o.raisable);
  if (raisableOrders.length === 0 && !infeasibleAtFloor) return new Map();

  // Income snapshots are computed against the input timeData once each; bunker consumption and
  // per-order port calls are captured once and reused across all snapshots so BAF/consumption and
  // shared costs do not shift priority between the floor and max/zero evaluations.
  const voyageCharterIds = new Set<VoyageCharterId>();
  for (const o of loadedOrders) {
    if (o.voyageCharterId !== null) voyageCharterIds.add(o.voyageCharterId);
  }
  const portCallIdsByOrder = getPortCallIdsByOrder(timeData);
  const bunkerConsumptionByPortCall = getConsumptionByPortCall(timeData);

  const sumIncomeByOrder = (
    amountOf: (o: LoadedOrder) => LoadedAmount,
  ): Map<OrderId, number> => {
    const incomes = getIncomeForLoadedAmount(
      loadedOrders.map(amountOf),
      voyageCharterIds,
      portCallIdsByOrder,
      bunkerConsumptionByPortCall,
    );
    const byOrder = new Map<OrderId, number>();
    for (const income of incomes) {
      if (income.orderId === null) continue;
      byOrder.set(
        income.orderId,
        (byOrder.get(income.orderId) ?? 0) + (income.value ?? 0),
      );
    }
    return byOrder;
  };

  // A raisable order at floor consumes floorRatio·max; a fixed order keeps its derived amount. Per-piece
  // freight bills on loadedPcs, so it must scale between the floor and max snapshots too: for a per-piece
  // order maxMass equals its max piece count (identity unit conversion), so loaded pieces track
  // floorMass..maxMass. Holding loadedPcs constant would make per-piece income identical at floor and max
  // ⇒ zero marginal ⇒ those orders rank last regardless of their rate.
  const raiseAmountAt = (o: LoadedOrder, atMax: boolean): LoadedAmount => ({
    orderId: o.orderId,
    loadedMass: !o.raisable ? o.fixedMass : atMax ? o.maxMass : o.floorMass,
    loadedVolume: !o.raisable
      ? o.fixedHold + o.fixedDeck
      : atMax
        ? o.maxHold + o.maxDeck
        : o.floorHold + o.floorDeck,
    loadedPcs:
      o.raisable && o.perPiece
        ? atMax
          ? o.maxMass
          : o.floorMass
        : o.loadedPcs,
    cargoAmount: o.cargoAmount,
    cargoUnit: o.cargoUnit,
  });

  // Reduce snapshots vary every order together across its whole band-floor load, so a fixed cargo has a
  // positive per-tonne denominator (floor-vs-zero, not the raise's floor-vs-max which is zero-width for
  // a fixed order).
  const reduceAmountAt = (o: LoadedOrder, ratio: number): LoadedAmount => ({
    orderId: o.orderId,
    loadedMass: o.maxMass * ratio,
    loadedVolume: (o.maxHold + o.maxDeck) * ratio,
    loadedPcs: o.perPiece ? o.maxMass * ratio : o.loadedPcs,
    cargoAmount: o.cargoAmount,
    cargoUnit: o.cargoUnit,
  });

  // Per-order chosen ratio, updated by the reduce and raise phases. In reduce mode every order is
  // modelled by maxDim·ratio (all seeded at floor); otherwise fixed orders keep their derived amount so
  // the raise-only path is unchanged.
  const chosenRatio = new Map<OrderId, number>();
  for (const o of loadedOrders) {
    if (o.raisable || infeasibleAtFloor)
      chosenRatio.set(o.orderId, o.floorRatio);
  }
  const ratioOf = (o: LoadedOrder): number =>
    chosenRatio.get(o.orderId) ?? o.floorRatio;
  const consumedMass = (o: LoadedOrder): number =>
    o.raisable || infeasibleAtFloor ? o.maxMass * ratioOf(o) : o.fixedMass;
  const consumedHold = (o: LoadedOrder): number =>
    o.raisable || infeasibleAtFloor ? o.maxHold * ratioOf(o) : o.fixedHold;
  const consumedDeck = (o: LoadedOrder): number =>
    o.raisable || infeasibleAtFloor ? o.maxDeck * ratioOf(o) : o.fixedDeck;

  const segUsage = (
    seg: OnBoardSegment,
  ): { usedMass: number; usedHold: number; usedDeck: number } => {
    let usedMass = 0;
    let usedHold = 0;
    let usedDeck = 0;
    for (const id of seg.onBoard) {
      const o = orderById.get(id);
      if (!o) continue;
      usedMass += consumedMass(o);
      usedHold += consumedHold(o);
      usedDeck += consumedDeck(o);
    }
    return { usedMass, usedHold, usedDeck };
  };

  // 1. Reduce phase: only when the set does not fit at minimum. Reduce the lowest marginal-income cargo
  // below its floor toward zero, last-loaded first on an income tie, until each over-full span clears.
  const reducedOrders = new Set<OrderId>();
  if (infeasibleAtFloor) {
    const reduceIncomeAtFloor = sumIncomeByOrder((o) =>
      reduceAmountAt(o, o.floorRatio),
    );
    const reduceIncomeAtZero = sumIncomeByOrder((o) => reduceAmountAt(o, 0));
    const reduceMarginalByOrder = new Map<OrderId, number>();
    for (const o of loadedOrders) {
      const deltaIncome =
        (reduceIncomeAtFloor.get(o.orderId) ?? 0) -
        (reduceIncomeAtZero.get(o.orderId) ?? 0);
      const denom = o.floorMass > 0 ? o.floorMass : o.floorHold + o.floorDeck;
      reduceMarginalByOrder.set(o.orderId, denom > 0 ? deltaIncome / denom : 0);
    }

    const reduceOrder = sortBy(loadedOrders, [
      (o) => reduceMarginalByOrder.get(o.orderId) ?? 0,
      (o) => -o.loadIndex,
    ]);
    for (const o of reduceOrder) {
      const indices = segmentIndicesByOrderId.get(o.orderId) ?? [];
      let delta = 0;
      for (const s of indices) {
        const seg = segments[s];
        const { usedMass, usedHold, usedDeck } = segUsage(seg);
        const massCap = Math.min(massCapacity, seg.draftMassCap);
        if (o.maxMass > 0) {
          const over = capOvershoot(usedMass, massCap);
          if (over > 0) delta = Math.max(delta, over / o.maxMass);
        }
        if (o.maxHold > 0) {
          const over = capOvershoot(usedHold, volumeCapacity);
          if (over > 0) delta = Math.max(delta, over / o.maxHold);
        }
        if (o.maxDeck > 0) {
          const over = capOvershoot(usedDeck, deckVolumeCapacity);
          if (over > 0) delta = Math.max(delta, over / o.maxDeck);
        }
      }
      if (delta <= 0) continue;
      const reducedContinuous = Math.max(0, ratioOf(o) - delta);
      const newRatio = o.perPiece
        ? snapReduceToWholePiece(o.maxMass, reducedContinuous)
        : reducedContinuous;
      chosenRatio.set(o.orderId, newRatio);
      reducedOrders.add(o.orderId);
    }
  }

  // 2. Raise phase: highest marginal income per tonne first, ties broken by orderId. Orders reduced
  // below floor are excluded so a reduction is never undone by a subsequent raise.
  const raiseCandidates = raisableOrders.filter(
    (o) => !reducedOrders.has(o.orderId),
  );
  if (raiseCandidates.length > 0) {
    const incomeAtFloor = sumIncomeByOrder((o) => raiseAmountAt(o, false));
    const incomeAtMax = sumIncomeByOrder((o) => raiseAmountAt(o, true));
    const marginalByOrder = new Map<OrderId, number>();
    for (const o of raiseCandidates) {
      const deltaMass = o.maxMass - o.floorMass;
      const deltaIncome =
        (incomeAtMax.get(o.orderId) ?? 0) - (incomeAtFloor.get(o.orderId) ?? 0);
      marginalByOrder.set(
        o.orderId,
        deltaMass > 0 ? deltaIncome / deltaMass : 0,
      );
    }

    const sortedRaisable = sortBy(raiseCandidates, [
      (o) => -(marginalByOrder.get(o.orderId) ?? 0),
      (o) => o.orderId,
    ]);
    for (const o of sortedRaisable) {
      const indices = segmentIndicesByOrderId.get(o.orderId) ?? [];
      let maxExtraRatio = 1 - o.floorRatio;
      for (const s of indices) {
        const seg = segments[s];
        const { usedMass, usedHold, usedDeck } = segUsage(seg);
        const deltaByMass =
          o.maxMass > 0 ? (massCapacity - usedMass) / o.maxMass : Infinity;
        const rem = o.deckCargo
          ? deckVolumeCapacity - usedDeck
          : volumeCapacity - usedHold;
        const maxVol = o.deckCargo ? o.maxDeck : o.maxHold;
        const deltaByVol = maxVol > 0 ? rem / maxVol : Infinity;
        // The laden port-draft mass cap is shared across the span's on-board orders, so it is charged
        // against the same live used mass as the DWCC headroom; an Infinity cap leaves the raise
        // bounded only by mass/volume.
        const deltaByDraft =
          o.maxMass > 0 ? (seg.draftMassCap - usedMass) / o.maxMass : Infinity;
        maxExtraRatio = Math.min(
          maxExtraRatio,
          deltaByMass,
          deltaByVol,
          deltaByDraft,
        );
      }
      const extraRatio = Math.max(0, maxExtraRatio);
      const continuousRatio = Math.min(1, o.floorRatio + extraRatio);
      // A per-piece order can only be loaded in whole pieces, so its raise is quantized DOWN to the
      // last whole piece the headroom allows. Snapping down only lowers consumption, so later raisable
      // orders still see ≥ this remaining capacity — the greedy stays capacity-safe.
      const newRatio = o.perPiece
        ? snapRaiseToWholePiece(
            o.maxMass,
            o.floorRatio,
            continuousRatio,
            perPieceFallbackRatio(o),
          )
        : continuousRatio;
      chosenRatio.set(o.orderId, newRatio);
    }
  }

  // 3. Result map. Raise-only mode returns exactly the raisable orders' ratios (fixed/no-band orders
  // omitted so DCAT keeps its default fill). Reduce mode returns every banded order plus any fixed
  // order actually reduced; unmoved fixed orders are omitted so DCAT does not re-inflate a
  // physically-capped fixed cargo back to its floor.
  const result: FillResult = new Map();
  if (infeasibleAtFloor) {
    for (const o of loadedOrders) {
      if (o.banded || reducedOrders.has(o.orderId)) {
        result.set(o.orderId, ratioOf(o));
      }
    }
  } else {
    for (const o of raiseCandidates) result.set(o.orderId, ratioOf(o));
  }
  return result;
}
